/**
 * Which URLs a link in a document artefact may point at.
 *
 * An href is CRDT content: written once, replicated to every member of the bid and rendered in
 * their editor. A `javascript:` (or `data:`) href is therefore stored XSS against the whole team,
 * not a mistake the author alone pays for. Only the three schemes bid prose actually needs are
 * allowed through — http, https and mailto.
 *
 * Two checks, on purpose:
 *  - `isLinkUrlAllowed` guards what a member TYPES in the Link dialog (EditorToolbar) — strict, an
 *    absolute URL and nothing else, so "example.com" is rejected instead of silently becoming a
 *    relative href that resolves against the SPA.
 *  - `isAllowedLinkUri` is handed to the tiptap Link mark (RichEditor) and covers every OTHER way a
 *    link can appear — paste, autolink, markdown parsed out of the CRDT, an AI edit.
 */

import { anchorIdOfHref } from '../../lib/anchors'

const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:']

/**
 * The scheme of an absolute URL, or null when it has none. Parsed with the platform's own URL
 * parser rather than a regex: it applies the same whitespace/control-character stripping the
 * browser applies before following an href, so `java\tscript:…` cannot slip past as "no scheme".
 */
function schemeOf(url: string): string | null {
  try {
    return new URL(url.trim()).protocol
  } catch {
    return null
  }
}

/** What the Link dialog accepts: an absolute http, https or mailto URL. */
export function isLinkUrlAllowed(url: string): boolean {
  const scheme = schemeOf(url)
  return scheme !== null && ALLOWED_SCHEMES.includes(scheme)
}

/**
 * tiptap's `Link.isAllowedUri` hook. Keeps the extension's own validation (`defaultValidate`, which
 * is what autolink feeds candidates like "www.example.com" through) and additionally refuses any
 * URL carrying a scheme outside the allow-list. Scheme-less values are left to `defaultValidate`,
 * so autolinking and relative hrefs keep behaving as before.
 */
export function isAllowedLinkUri(
  url: string,
  ctx: { defaultValidate: (url: string) => boolean },
): boolean {
  // A link to an anchor of the artefact (AB#350): `#anchor-<id>`, a valid id only. Explicit rather
  // than left to tiptap's "starts with a non-letter" leniency, so it never depends on it. (Any other
  // fragment keeps today's treatment: refusing it would drop links already in parts.)
  if (anchorIdOfHref(url) !== null) return true
  const scheme = schemeOf(url)
  return (scheme === null || ALLOWED_SCHEMES.includes(scheme)) && ctx.defaultValidate(url)
}
